/**
 * Site Configuration Generator
 *
 * Generates site sections dynamically based on company data from the API.
 * Each tenant gets a personalized site driven by:
 *   - companyData.company_name
 *   - companyData.branding_logo
 *   - companyData.branding_colour
 *   - companyData.industry_category  (e.g. "Hospital")
 *   - companyData.doctors[]
 *   - companyData.testimonials[]
 *   - companyData.total_bookings / total_doctors / total_users
 *   - companyData.site_content (parsed JSON from uploaded site_content_doc)
 *
 * Priority: site_content JSON > TENANT_OVERRIDES > INDUSTRY_DEFAULTS
 */

// Build the CSS transform for the logo: free translate offset ("x% y%",
// where 0% 0% = centered) + free zoom. Stored in companyData.logo_position
// (offset) and companyData.logo_scale (zoom).
function buildLogoTransform(position, scale) {
  const z = Number(scale) || 1;
  let tx = '0%';
  let ty = '0%';
  const m = String(position || '').match(/(-?\d+(?:\.\d+)?)%\s+(-?\d+(?:\.\d+)?)%/);
  if (m) {
    tx = `${m[1]}%`;
    ty = `${m[2]}%`;
  }
  const parts = [];
  if (tx !== '0%' || ty !== '0%') parts.push(`translate(${tx}, ${ty})`);
  if (z !== 1) parts.push(`scale(${z})`);
  return parts.length ? parts.join(' ') : undefined;
}

// ── Per-tenant overrides (keyed by company_id) ──────────────────────────
const TENANT_OVERRIDES = {
  54: {
    tagline: 'Natural Healing. Personal Care. Trusted Results.',
    heroSubtext:
      'At Adithya Homeo Clinics, we provide gentle, effective, and personalized homeopathic treatment focused on long-term healing.',
    heroExtraParagraphs: [
      'With multiple clinics serving patients across Kerala, we are trusted by families for our ethical approach, careful diagnosis, and visible improvement in chronic and recurring health conditions.',
      'Our patients consistently highlight our patient listening, clarity of treatment, and gradual but lasting results — which is why many come through word-of-mouth and Google reviews.',
      'We believe true healing begins when the body is treated as a whole, not in parts.',
    ],
    whyChooseUs: [
      'Experienced and compassionate homeopathic doctors',
      'Individualized treatment plans for every patient',
      'Focus on root-cause treatment, not symptom suppression',
      'Safe, natural, and side-effect-free medicines',
      'Highly rated on Google across multiple clinic locations',
    ],
    conditions: [
      'Skin diseases (eczema, psoriasis, acne, allergies)',
      'Thyroid disorders & diabetes support',
      'Asthma, sinusitis & respiratory allergies',
      'Digestive issues & gastric complaints',
      'Hair fall & scalp problems',
      "Women's & children's health concerns",
      'Stress, anxiety & sleep-related issues',
    ],
    conditionsTagline: 'Healing that works with your body — not against it.',
    aboutWhoWeAre:
      'Adithya Homeo Clinics is a group of homeopathic clinics providing care through classical homeopathy. Our doctors take time to understand:',
    aboutWhoWeAreBullets: [
      'Your medical history',
      'Your lifestyle and triggers',
      'Your physical and emotional health',
    ],
    aboutWhoWeAreNote:
      'This detailed consultation process is one of the most appreciated aspects mentioned in patient reviews.',
    aboutApproach: [
      "Strengthening the body's natural immunity",
      'Treating the underlying cause of illness',
      'Providing sustainable improvement rather than quick, temporary relief',
    ],
    aboutApproachNote:
      'Patients often share that they feel heard, reassured, and confident after consultations — a trust we value deeply.',
    aboutMission:
      'To make high-quality homeopathic care accessible, transparent, and effective across our clinics, helping people lead healthier, balanced lives through natural healing.',
    locations: [
      'Kaloor – Ernakulam',
      'Thoppumpady – Kochi',
      'Elamakkara – Ernakulam',
    ],
    googleMapsNote:
      'Read genuine patient reviews and get directions easily through Google Maps for each clinic location.',
    closingCta:
      'Whether you are dealing with a chronic condition or a recurring health issue, our team is here to guide you with care, clarity, and confidence.',
    footerTagline: 'Adithya Homeo Clinics — Healing from within, naturally',
    contactIntro:
      'Adithya Homeo Clinics operates through multiple conveniently located clinics. You may choose the branch closest to you for consultation and follow-up care.',
    consultationHours: 'Monday – Saturday',
  },
};

// ── Industry-specific defaults ──────────────────────────────────────────
const INDUSTRY_DEFAULTS = {
  hospital: {
    tagline: 'Natural Healing. Personal Care. Trusted Results.',
    heroSubtext: (name) =>
      `At ${name}, we provide gentle, effective, and personalized treatment focused on long-term healing.`,
    whyChooseUs: [
      'Experienced and compassionate doctors',
      'Individualized treatment plans for every patient',
      'Focus on root-cause treatment, not symptom suppression',
      'Safe, natural, and side-effect-free medicines',
      'Highly rated on Google across multiple clinic locations',
    ],
    conditions: [
      'Skin diseases (eczema, psoriasis, acne, allergies)',
      'Thyroid disorders & diabetes support',
      'Asthma, sinusitis & respiratory allergies',
      'Digestive issues & gastric complaints',
      'Hair fall & scalp problems',
      "Women's & children's health concerns",
      'Stress, anxiety & sleep-related issues',
    ],
    conditionsTagline: 'Healing that works with your body — not against it.',
    aboutWhoWeAre: (name) =>
      `${name} is committed to providing ethical, patient-centered healthcare. Our doctors take time to understand your medical history, lifestyle, triggers, and overall health. This detailed consultation process is one of the most appreciated aspects mentioned in patient reviews.`,
    aboutApproach: [
      "Strengthening the body's natural immunity",
      'Treating the underlying cause of illness',
      'Providing sustainable improvement rather than quick, temporary relief',
    ],
    aboutApproachNote:
      'Patients often share that they feel heard, reassured, and confident after consultations — a trust we value deeply.',
    aboutMission: (name) =>
      `To make high-quality care accessible, transparent, and effective across our clinics, helping people lead healthier, balanced lives through natural healing.`,
    footerTagline: (name) => `${name} — Healing from within, naturally`,
    contactIntro: (name) =>
      `${name} operates through multiple conveniently located clinics. You may choose the branch closest to you for consultation and follow-up care.`,
    consultationHours: 'Monday – Saturday',
    statLabels: {
      bookings: 'Total Bookings',
      doctors: 'Expert Doctors',
      users: 'Happy Patients',
      extra: { value: 'Mon–Sat', label: 'Consultation Days' },
    },
    features: [
      { title: 'Trusted Care', desc: 'Patient-centered treatment with proven results', icon: 'heart' },
      { title: 'Expert Doctors', desc: 'Experienced and compassionate medical professionals', icon: 'person' },
      { title: 'Easy Booking', desc: 'Book appointments online, anytime', icon: 'calendar' },
    ],
  },

  // ── Generic fallback for non-hospital tenants ─────────────────────────
  default: {
    tagline: 'Excellence in Every Service.',
    heroSubtext: (name) => `Experience the best in ${name} services. We're here to serve you better.`,
    whyChooseUs: null,
    conditions: null,
    aboutWhoWeAre: (name) =>
      `Welcome to ${name}! We are a team of dedicated professionals committed to providing exceptional service to our customers.`,
    aboutApproach: null,
    aboutMission: () =>
      'Our mission is to deliver personalized services that meet the unique needs of each customer. We believe in a customer-first approach, combining expertise with compassionate care.',
    footerTagline: () => 'Your satisfaction, our priority',
    statLabels: {
      bookings: 'Total Bookings',
      doctors: 'Expert Staff',
      users: 'Happy Customers',
      extra: { value: '24/7', label: 'Support' },
    },
    features: [
      { title: 'Fast Service', desc: 'Quick and efficient service delivery', icon: 'clock' },
      { title: 'Expert Team', desc: 'Professional and experienced staff', icon: 'person' },
      { title: '24/7 Support', desc: 'Round-the-clock customer support', icon: 'globe' },
    ],
  },
};

// ── Hospital keyword matcher ────────────────────────────────────────────
const HOSPITAL_KEYWORDS = ['hospital', 'clinic', 'homeo', 'health', 'medical', 'pharma'];

function detectIndustry(companyData) {
  const category =
    typeof companyData?.industry_category === 'string'
      ? companyData.industry_category
      : companyData?.industry_category?.name || '';

  const isHospital = HOSPITAL_KEYWORDS.some(
    (kw) =>
      category.toLowerCase().includes(kw) ||
      companyData?.company_name?.toLowerCase().includes(kw)
  );

  return isHospital ? 'hospital' : 'default';
}

// ── Map uploaded site-content JSON to config fields ─────────────────────
function mapSiteContentToConfig(siteContent, name) {
  if (!siteContent || siteContent._schema !== 'site-content-v1') return {};

  const mapped = {};

  // Hero section
  if (siteContent.hero) {
    if (siteContent.hero.tagline) mapped.tagline = siteContent.hero.tagline;
    if (siteContent.hero.subtext) mapped.heroSubtext = siteContent.hero.subtext;
  }

  // Why Choose Us
  if (siteContent.whyChooseUs) mapped.whyChooseUs = siteContent.whyChooseUs;

  // Features
  if (siteContent.features) mapped.features = siteContent.features;

  // Conditions
  if (siteContent.conditions) {
    if (Array.isArray(siteContent.conditions)) {
      mapped.conditions = siteContent.conditions;
    } else {
      mapped.conditions = siteContent.conditions.items || null;
      if (siteContent.conditions.tagline) mapped.conditionsTagline = siteContent.conditions.tagline;
    }
  }

  // About section
  if (siteContent.about) {
    if (siteContent.about.whoWeAre) mapped.aboutWhoWeAre = siteContent.about.whoWeAre;
    if (siteContent.about.approach) mapped.aboutApproach = siteContent.about.approach;
    if (siteContent.about.approachNote) mapped.aboutApproachNote = siteContent.about.approachNote;
    if (siteContent.about.mission) mapped.aboutMission = siteContent.about.mission;
  }

  // Stats labels + admin value overrides
  if (siteContent.stats?.labels) mapped.statLabels = siteContent.stats.labels;
  if (siteContent.stats?.values) mapped.statValues = siteContent.stats.values;

  // Footer
  if (siteContent.footer) {
    if (siteContent.footer.tagline) mapped.footerTagline = siteContent.footer.tagline;
    if (siteContent.footer.contactIntro) mapped.contactIntro = siteContent.footer.contactIntro;
    if (siteContent.footer.consultationHours) mapped.consultationHours = siteContent.footer.consultationHours;
  }

  // Locations
  if (siteContent.locations) mapped.locations = siteContent.locations;

  return mapped;
}

// ── Map DB sections (array of {section_type, content}) to flat config ──
function mapDbSectionsToConfig(dbSections) {
  if (!dbSections || !Array.isArray(dbSections) || dbSections.length === 0) return {};

  const mapped = {};
  for (const section of dbSections) {
    const { section_type, content } = section;
    if (!content) continue;

    switch (section_type) {
      case 'hero':
        if (content.tagline) mapped.tagline = content.tagline;
        if (content.subtext) mapped.heroSubtext = content.subtext;
        if (content.extra_paragraphs) mapped.heroExtraParagraphs = content.extra_paragraphs;
        if (content.background_image) mapped.heroBannerImage = content.background_image;
        break;
      case 'why_choose_us':
        if (content.items) mapped.whyChooseUs = content.items;
        break;
      case 'conditions':
        if (content.items) mapped.conditions = content.items;
        if (content.tagline) mapped.conditionsTagline = content.tagline;
        break;
      case 'about':
        if (content.who_we_are) mapped.aboutWhoWeAre = content.who_we_are;
        if (content.who_we_are_bullets) mapped.aboutWhoWeAreBullets = content.who_we_are_bullets;
        if (content.who_we_are_note) mapped.aboutWhoWeAreNote = content.who_we_are_note;
        if (content.approach) mapped.aboutApproach = content.approach;
        if (content.approach_note) mapped.aboutApproachNote = content.approach_note;
        if (content.mission) mapped.aboutMission = content.mission;
        if (content.gallery_images) mapped.galleryImages = content.gallery_images;
        if (content.gallery_images_adjust) mapped.imageAdjust = { ...(mapped.imageAdjust || {}), ...content.gallery_images_adjust };
        break;
      case 'gallery':
        if (content.images) mapped.galleryCarousel = content.images;
        if (content.title) mapped.galleryTitle = content.title;
        if (content.images_adjust) mapped.imageAdjust = { ...(mapped.imageAdjust || {}), ...content.images_adjust };
        break;
      case 'features':
        if (content.items) mapped.features = content.items;
        break;
      case 'locations':
        if (content.items) mapped.locations = content.items;
        if (content.google_maps_note) mapped.googleMapsNote = content.google_maps_note;
        break;
      case 'doctors':
        mapped.doctorsSection = {
          enabled: content.enabled !== false, // default ON
          title: content.title || 'Meet Our Doctors',
        };
        break;
      case 'footer':
        if (content.tagline) mapped.footerTagline = content.tagline;
        if (content.contact_intro) mapped.contactIntro = content.contact_intro;
        if (content.consultation_hours) mapped.consultationHours = content.consultation_hours;
        break;
      case 'closing_cta':
        if (content.text) mapped.closingCta = content.text;
        break;
      case 'stats':
        if (content.labels) mapped.statLabels = content.labels;
        // Admin overrides for stat values (blank = auto-calc; 0 = hide).
        if (content.bookings !== undefined || content.users !== undefined) {
          mapped.statValues = {
            ...(content.bookings !== undefined ? { bookings: content.bookings } : {}),
            ...(content.users !== undefined ? { users: content.users } : {}),
          };
        }
        break;
    }
  }
  return mapped;
}

// ── Public API ──────────────────────────────────────────────────────────
export function getSiteConfig(companyData, siteContentJson = null, dbSections = null) {
  if (!companyData) return { ...INDUSTRY_DEFAULTS.default, industry: 'default', isHospital: false };

  const industry = detectIndustry(companyData);
  const defaults = INDUSTRY_DEFAULTS[industry] || INDUSTRY_DEFAULTS.default;
  const overrides = TENANT_OVERRIDES[companyData.company_id] || {};
  const name = companyData.company_name || 'Our Company';

  // Parse uploaded site content doc (highest priority)
  const siteContent = mapSiteContentToConfig(siteContentJson, name);

  // Map DB sections (replaces hardcoded overrides over time)
  const dbConfig = mapDbSectionsToConfig(dbSections);

  // Merge: defaults < hardcoded overrides < DB sections < uploaded JSON
  const merged = {
    ...defaults,
    ...overrides,
    ...dbConfig,
    ...siteContent,
  };

  // Resolve heroSubtext — could be a function (from defaults) or a string (from JSON upload)
  const resolveStringOrFn = (val, fallback) => {
    if (typeof val === 'string') return val;
    if (typeof val === 'function') return val(name);
    if (typeof fallback === 'function') return fallback(name);
    return fallback || '';
  };

  // Prefer backend-managed locations (array of {id, name, address, phone})
  // over legacy string-based overrides / site_content JSON.
  const backendLocations = Array.isArray(companyData.locations) ? companyData.locations : [];
  const resolvedLocations = backendLocations.length > 0
    ? backendLocations
    : (siteContent.locations || overrides.locations || []);

  // Stat values: admin override wins when set (blank/undefined = auto-calculate;
  // 0 is a real value → the stat is hidden by the templates' zero-check).
  const sv = merged.statValues || {};
  const pickStat = (override, auto) => {
    if (override === undefined || override === null || override === '') return auto ?? 0;
    const n = parseInt(override, 10);
    return Number.isNaN(n) ? (auto ?? 0) : n;
  };

  return {
    ...merged,
    industry,
    isHospital: industry === 'hospital',
    companyName: name,
    logo: companyData.branding_logo || null,
    logoTransform: buildLogoTransform(companyData.logo_position, companyData.logo_scale),
    accentColor: companyData.branding_colour || '#c6922b',
    contact_email: companyData.contact_email || null,
    contact_phone: companyData.contact_phone || null,
    whatsapp_enabled: !!companyData.whatsapp_enabled,
    whatsapp_message: companyData.whatsapp_message || '',
    stats: {
      bookings: pickStat(sv.bookings, companyData.total_bookings),
      doctors: companyData.total_doctors ?? 0,
      users: pickStat(sv.users, companyData.total_users),
    },
    statLabels: merged.statLabels || defaults.statLabels,
    doctors: companyData.doctors || [],
    doctorsSection: merged.doctorsSection || { enabled: true, title: 'Meet Our Doctors' },
    testimonials: companyData.testimonials || [],
    locations: resolvedLocations,
    // Resolve function-or-string content
    heroSubtext: resolveStringOrFn(merged.heroSubtext, defaults.heroSubtext),
    aboutWhoWeAre: resolveStringOrFn(merged.aboutWhoWeAre, defaults.aboutWhoWeAre),
    aboutMission: resolveStringOrFn(merged.aboutMission, defaults.aboutMission),
    footerTagline: resolveStringOrFn(merged.footerTagline, defaults.footerTagline),
    contactIntro: resolveStringOrFn(merged.contactIntro, defaults.contactIntro),
  };
}
